import React, { useEffect, useState } from "react";
import { Link, Route, Switch, useHistory, useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client";
import {
  ApiRequest,
  getQueryParameter,
  ProfileData,
  WHATA_ENDPOINTS,
  MUTATE_LOGIN,
} from "@plinknz/tah-website-elements";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-d560caf08c/icons/modules";

import { Hero } from "../../../components/hero";
import * as ROUTES from "../../../config/router";
import { whataRequest } from "../../../service/member/whata";
import { UserContext } from "../../../service/reducers/user";
import { Loader } from "../../../components/loader";
import { EmailLoginForm } from "./email/email-login";
import { ResetPassword } from "./email/reset-password";
import { NewPasswordForm } from "./email/new-password";
import { SetupLogin } from "./email/setup-login";

interface LoginProps {
  whata?: ApiRequest;
}

interface LoginState {
  referrer: string;
}

export const Login = ({ whata = whataRequest }: LoginProps) => {
  const {
    state: { loading },
    dispatch,
  } = React.useContext(UserContext);
  const [redirect, setRedirect] = useState(ROUTES.members());
  const history = useHistory();
  const location = useLocation();
  const state = location.state as LoginState;

  useEffect(() => {
    if (state?.referrer && state?.referrer !== "/mema") {
      setRedirect(state.referrer);
    }
  }, [state]);

  const page = location.pathname.replace(/^.*\/([^/]+)$/, "$1");
  let title: string;

  switch (page) {
    case "reset":
      title = "Reset Password";
      break;
    case "setup":
      title = "Setup Login";
      break;
    case "new-password":
      title = "Set Password";
      break;
    default:
      title = "Login";
  }

  const [strapiLogin] = useMutation(MUTATE_LOGIN);

  const loginWithToken = async (token: string) => {
    dispatch({ type: "loading", isLoading: true });

    try {
      // eslint-disable-next-line no-param-reassign
      whata.token = token;
      const { data } = await whata.get<ProfileData>(WHATA_ENDPOINTS.me());

      if (data) {
        dispatch({
          type: "update_user",
          user: {
            id: data.id.toString(),
            email: data.email,
            name: `${data.firstName} ${data.surname}`,
            onSameEmail: data.onSameEmail,
          },
        });

        const variables = {
          email: data.email,
          token: whata.token,
        };

        const loginResponse = await strapiLogin({
          variables,
        });

        dispatch({
          type: "set_tokens",
          payload: {
            strapiToken: loginResponse.data.login.jwt as string,
            whataToken: token,
          },
        });
        dispatch({ type: "set_logged_in", isLoggedIn: true });

        history.push(redirect);
      }
    } catch (loginError: unknown) {
      console.warn(loginError);

      if (location.pathname !== ROUTES.membersLogin()) {
        dispatch({
          type: "error",
          error:
            "Could not log you in. Please make sure you are registered with Rangitāne or email us.",
        });
        history.push(ROUTES.membersLogin());
      }
    } finally {
      dispatch({ type: "loading", isLoading: false });
    }
  };

  function checkAndHandleToken() {
    const token = getQueryParameter("token");

    if (token) {
      loginWithToken(token);
    } else {
      dispatch({ type: "reset" });
    }
  }

  function setError() {
    const errors = getQueryParameter("FBError");
    dispatch({ type: "error", error: errors });
  }

  function renderOptions() {
    return (
      <div className="button-list">
        <Link
          to={ROUTES.membersLoginWithEmail()}
          className="button large primary"
          data-testid="login-email"
        >
          <FontAwesomeIcon
            icon={byPrefixAndName.fas.envelope}
            className="button-icon left"
          />
          Login
        </Link>
        <Link
          to={ROUTES.membersLoginSetup()}
          className="button large primary"
          data-testid="setup-login"
        >
          <FontAwesomeIcon
            icon={byPrefixAndName.fas.key}
            className="button-icon left"
          />
          Setup Login
        </Link>
      </div>
    );
  }

  useEffect(() => {
    checkAndHandleToken();
    setError();
  }, []);

  return (
    <div className="main" data-testid="login">
      <Hero title={title} />
      {(loading && (
        <div className="splash">
          <Loader />
        </div>
      )) || (
        <div className="constrain-width small">
          <div className="content">
            <Switch>
              <Route path={ROUTES.membersLoginWithEmail()}>
                <EmailLoginForm loginWithToken={loginWithToken} />
              </Route>
              <Route path={ROUTES.membersLoginReset()}>
                <ResetPassword />
              </Route>
              <Route path={ROUTES.membersLoginSetup()}>
                <SetupLogin />
              </Route>
              <Route path={ROUTES.membersLoginNewPassword()}>
                <NewPasswordForm />
              </Route>
              <Route path="*">{renderOptions()}</Route>
            </Switch>
          </div>
        </div>
      )}
    </div>
  );
};
